import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Card, EmptyState, Spinner, Label } from "@/components/ui";
import { fmtDateTime } from "@/lib/helpers";
import { PilotBanner } from "@/components/billing/PilotBanner";
import { Pill, CalendarDays, Users, Mail, ChevronRight, Activity, Stethoscope, User } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

function Stat({ label, value, to, testid }) {
  const inner = (
    <div className="bg-brand-tint border border-line rounded-xl px-4 py-3 h-full" data-testid={testid}>
      <div className="font-display text-2xl text-brand-dark">{value}</div>
      <div className="text-xs text-subtle mt-0.5">{label}</div>
    </div>
  );
  return to ? <Link to={to} className="block hover:-translate-y-0.5 transition-transform">{inner}</Link> : inner;
}

function Section({ title, to, children }) {
  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl text-ink">{title}</h2>
        {to && <Link to={to} className="text-sm font-semibold text-brand hover:underline">See all</Link>}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { family, patient } = useAuth();
  if (!family && patient) return <PatientHome />;
  return <FamilyHome />;
}

function PatientHome() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.patientMe().then(setData).catch(() => setData({})); }, []);
  if (!data) return <Spinner />;
  const p = data.patient || {};
  const done = p.completeness?.done ?? [];
  const missing = p.completeness?.missing ?? [];
  return (
    <div className="kb-fade-up">
      <div className="text-xs font-semibold text-brand uppercase tracking-wide">Your private space</div>
      <h1 className="font-display text-3xl sm:text-4xl text-ink mt-1" data-testid="patient-greeting">
        Hello, {(user?.name || p.name || "there").split(" ")[0]}
      </h1>
      <p className="text-subtle mt-1">This is your personal KinBridge health space{data.family_name ? `, supported by ${data.family_name}` : ""}.</p>

      <Card className="mt-6">
        <Label>Your profile</Label>
        <div className="mt-2 space-y-1 text-[15px] text-ink">
          <div><span className="text-subtle">Name:</span> {p.name || "—"}</div>
          {p.dob && <div><span className="text-subtle">Date of birth:</span> {p.dob}</div>}
          {p.conditions && <div><span className="text-subtle">Conditions:</span> {p.conditions}</div>}
        </div>
      </Card>

      {missing.length > 0 && (
        <Card className="mt-4 bg-mint-tint border-mint-dark">
          <Label className="text-ink">Complete your profile</Label>
          <p className="text-sm text-subtle mt-1">Add these in the KinBridge mobile app to help your care circle support you:</p>
          <div className="mt-2 space-y-1">{missing.map((m) => <div key={m} className="text-sm text-ink">• {m}</div>)}</div>
        </Card>
      )}

      {(data.circle || []).length > 0 && (
        <div className="mt-4">
          <h2 className="font-display text-xl text-ink mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-brand" /> Your care circle</h2>
          <Card className="p-0 overflow-hidden">
            {data.circle.map((m, i) => (
              <div key={`${m.name}-${m.role}-${i}`} className={`flex items-center gap-3 p-4 ${i > 0 ? "border-t border-line" : ""}`}>
                <div className="w-9 h-9 rounded-full bg-brand flex items-center justify-center text-white font-semibold">{m.name?.[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0"><div className="font-medium text-ink truncate">{m.name}</div><div className="text-xs text-subtle capitalize">{(m.role || "").replace(/_/g, " ")}</div></div>
              </div>
            ))}
          </Card>
        </div>
      )}

      <Card className="mt-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-tint flex items-center justify-center shrink-0"><Activity className="w-5 h-5 text-brand-dark" /></div>
          <div>
            <div className="font-semibold text-ink">Get the full experience</div>
            <p className="text-sm text-subtle mt-1">Manage insurance, conditions, and connected wearables from the KinBridge mobile app on your phone.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function FamilyHome() {
  const { user, family, role } = useAuth();
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const [a, m, l, e, t, u, p] = await Promise.all([
        api.activity(), api.listMeds(), api.getMedLogs(todayISO()),
        api.listEvents(), api.visitTasks("pending").catch(() => []),
        api.messagesUnreadCount().catch(() => ({ unread: 0 })),
        api.listPatients().catch(() => ({ items: [] })),
      ]);
      setData({ activity: a, meds: m, logs: l, events: e, tasks: t, unread: u?.unread ?? 0, patients: p.items || [] });
    } catch { setData({ activity: [], meds: [], logs: [], events: [], tasks: [], unread: 0, patients: [] }); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <Spinner />;

  const { activity, meds, logs, events, tasks, unread, patients } = data;
  const today = todayISO();
  const doses = meds.flatMap((m) => (m.schedule_times || []).map((t) => ({ med: m, time: t })));
  const taken = logs.filter((l) => l.status === "taken").length;
  const upcoming = events.filter((e) => new Date(e.starts_at).getTime() >= Date.now())
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)).slice(0, 3);
  const primary = patients.find((x) => x.is_primary) || patients[0];

  const markTask = async (id) => { await api.updateVisitTask(id, "done"); await load(); };

  return (
    <div className="kb-fade-up">
      <div className="text-xs font-semibold text-brand uppercase tracking-wide">
        Today · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
      </div>
      <h1 className="font-display text-3xl sm:text-4xl text-ink mt-1" data-testid="home-greeting">
        Hello, {user?.name?.split(" ")[0] || "there"}
      </h1>
      <p className="text-subtle mt-1">
        Caring for <span className="font-semibold text-ink">{primary?.name || family?.loved_one?.name || "your loved one"}</span>
        {patients.length > 1 ? ` + ${patients.length - 1} more` : ""}
        {role ? ` · ${role.replace(/_/g, " ")}` : ""}
      </p>

      <PilotBanner />

      <div className="grid grid-cols-3 gap-3 mt-6">
        <Stat label="Doses today" value={`${taken}/${doses.length}`} testid="stat-meds" to="/app/meds" />
        <Stat label="Upcoming" value={String(upcoming.length)} testid="stat-events" to="/app/calendar" />
        <Stat label={patients.length === 1 ? "Care recipient" : "Care recipients"} value={String(patients.length || 1)} testid="stat-patients" to="/app/patients" />
      </div>

      {unread > 0 && (
        <Link to="/app/inbox" data-testid="home-inbox-banner"
          className="mt-5 flex items-center gap-3 bg-white border border-brand-dark rounded-xl p-4 hover:bg-brand-tint transition-colors">
          <div className="relative w-11 h-11 rounded-full bg-brand flex items-center justify-center">
            <Mail className="w-5 h-5 text-white" />
            <span className="absolute -top-1 -right-1 bg-mint text-brand-dark text-[10px] font-bold rounded-full px-1.5 border-2 border-surface">{unread > 9 ? "9+" : unread}</span>
          </div>
          <div className="flex-1">
            <div className="font-semibold text-ink">{unread === 1 ? "1 new message" : `${unread} new messages`}</div>
            <div className="text-sm text-subtle">From your KinBridge advocate · tap to read</div>
          </div>
          <ChevronRight className="w-5 h-5 text-subtle" />
        </Link>
      )}

      {tasks.length > 0 && (
        <Section title="Tasks from visits" to="/app/scribe">
          <Card className="p-0 overflow-hidden">
            {tasks.slice(0, 5).map((t, i) => (
              <div key={t.id} className={`flex items-center gap-3 p-4 ${i > 0 ? "border-t border-line" : ""}`}>
                <button data-testid={`task-toggle-${t.id}`} onClick={() => markTask(t.id)}
                  className="w-7 h-7 rounded-full border-2 border-brand hover:bg-brand-tint shrink-0" />
                <div className="flex-1">
                  <div className="text-[15px] text-ink">{t.title}</div>
                  <div className="text-xs text-subtle">{t.kind === "follow_up" ? "Follow-up" : "Family task"} · from “{t.source_visit_title}”</div>
                </div>
              </div>
            ))}
          </Card>
        </Section>
      )}

      <Section title="Meds due today" to="/app/meds">
        {doses.length === 0 ? (
          <Card><EmptyState testid="home-empty-meds" icon={Pill} title="No doses scheduled yet"
            body="Add a medication and we'll track every scheduled dose." /></Card>
        ) : (
          <Card className="p-0 overflow-hidden">
            {doses.slice(0, 4).map(({ med, time }, i) => {
              const done = logs.find((l) => l.med_id === med.id && l.scheduled_time === time && l.date === today && l.status === "taken");
              return (
                <div key={med.id + time} className={`flex items-center p-4 ${i > 0 ? "border-t border-line" : ""}`}>
                  <div className="flex-1">
                    <div className="font-semibold text-ink">{med.name}</div>
                    <div className="text-sm text-subtle">{med.dosage} · {time}</div>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${done ? "bg-ok text-white" : "bg-brand-tint text-brand-dark border border-line"}`}>
                    {done ? "Taken" : "Pending"}
                  </span>
                </div>
              );
            })}
          </Card>
        )}
      </Section>

      <Section title="Upcoming" to="/app/calendar">
        {upcoming.length === 0 ? (
          <Card><EmptyState testid="home-empty-events" icon={CalendarDays} title="Nothing on the calendar"
            body="Appointments and aide shifts you schedule will show up here." /></Card>
        ) : (
          <Card className="p-0 overflow-hidden">
            {upcoming.map((e, i) => (
              <div key={e.id} className={`flex items-center gap-3 p-4 ${i > 0 ? "border-t border-line" : ""}`}>
                <div className="w-9 h-9 rounded-lg bg-brand-tint flex items-center justify-center">
                  {e.kind === "aide_shift" ? <User className="w-[18px] h-[18px] text-brand-dark" /> : <Stethoscope className="w-[18px] h-[18px] text-brand-dark" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-ink truncate">{e.title}</div>
                  <div className="text-sm text-subtle">{fmtDateTime(e.starts_at)}</div>
                </div>
              </div>
            ))}
          </Card>
        )}
      </Section>

      <Section title="Recent activity">
        {activity.length === 0 ? (
          <Card><EmptyState testid="home-empty-activity" icon={Activity} title="Your circle is quiet"
            body="Doses, appointments and sign-offs will appear here as your circle takes action." /></Card>
        ) : (
          <Card className="p-0 overflow-hidden">
            {activity.slice(0, 6).map((a, i) => (
              <div key={a.id} className={`flex gap-3 p-4 ${i > 0 ? "border-t border-line" : ""}`}>
                <div className="w-2 h-2 rounded-full bg-brand mt-2 shrink-0" />
                <div className="flex-1">
                  <div className="text-[15px] text-ink">{a.message}</div>
                  <div className="text-xs text-subtle">{fmtDateTime(a.created_at)}</div>
                </div>
              </div>
            ))}
          </Card>
        )}
      </Section>
    </div>
  );
}
